<template>

  <v-container fluid class="mb-6 page-content route-list-table">

    <!----------------------------->
    <!-- Page Label and Controls -->
    <!----------------------------->
    <v-container fluid class="pa-0">
      <v-row>
        <v-col cols="12" xs="12" sm="12" md="12" lg="12">
          <span class="mr-3 page-label">{{ title }}</span>
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="info-timezone" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <div>Dates & times are: {{ activeTimezone }}</div>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-row>
        <!-- Find by route name -->
        <v-col cols="12" xs="12" sm="12" md="7" lg="5" class="d-flex align-center justify-center justify-sm-start">
          <v-text-field
              clearable
              single-line
              hide-details
              style="max-width:300px;"
              class="ml-2 mb-2"
              label="Route name"
              append-icon="mdi-magnify"
              v-model="search" />
        </v-col>
        <!-- Filter by Live and Test -->
        <v-col cols="12" xs="12" sm="12" md="5" lg="4" class="">
          <div class="page-super-title">Show</div>
          <div class="d-flex align-center">
            <v-checkbox
                class="mt-0"
                label="Live Routes"
                hide-details
                @change="queryData"
                v-model="showLiveRoutes" />
            <v-checkbox
                class="pl-8 mt-0"
                label="Test Routes"
                hide-details
                @change="queryData"
                v-model="showTestRoutes" />
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!---------------->
    <!-- Route List -->
    <!---------------->
    <div class="mt-8">
      <v-data-table
          class="list-routes"
          :headers="headers"
          :items="routeItems"
          :search="search"
          :options.sync="dataOptions"
          :footer-props="paginationOptions"
          :loading="isDataLoading"
          :server-items-length="totalItems"
          :show-select="isSelectable"
          v-model="selected">

          <!-- route name -->
          <template v-slot:[`item.name`]="{ item }">
            <!-- Test Route indicator -->
            <div class="mb-2" v-if="item.is_test">
              <v-chip label color="orange darken-4" style="font-weight:500;">TEST ROUTE</v-chip>
            </div>
            <span style="font-size:1.15em">
              <router-link :to="generateRouteClickPageLink(item)">
                {{ item.name }}
              </router-link>
            </span>
          </template>

          <!-- delivery date -->
          <template v-slot:[`item.delivery_date`]="{ item }">
            <span class="delivery_date">{{ formatDate(item.delivery_date) }}</span>
          </template>

          <!-- start time -->
          <template v-slot:[`item.scheduled_start_time`]="{ item }">
            <span class="scheduled_start_time">{{ formatTime(item.scheduled_start_time) }}</span>
          </template>

          <!-- return time -->
          <template v-slot:[`item.estimated_return_time`]="{ item }">
            <span class="estimated_return_time">{{ formatTime(item.estimated_return_time) }}</span>
          </template>

          <!-- orders -->
          <template v-slot:[`item.orders`]="{ item }">
            <div class="mt-2" v-if="item.orders.length > 0" />
            <router-link :to="generateOrderInfoPageLink(order)" v-for="order in item.orders" :key="order.order_number">
              <v-chip
                  v-if="order.order_status === 'RESCHEDULED'"
                  class="mb-2"
                  label
                  outlined
                  color="yellow accent-4">
                  <v-icon left style="font-size:19px;">mdi-calendar-refresh</v-icon>
                {{ order.thirdparty_order_number }}
              </v-chip>
              <v-chip
                  v-else-if="order.order_status === 'CANCELED'"
                  class="mb-2"
                  label
                  outlined
                  color="red darken-3">
                  <v-icon left style="font-size:19px;">mdi-cancel</v-icon>
                {{ order.thirdparty_order_number }}
              </v-chip>
              <v-chip
                  v-else
                  class="mb-2"
                  label
                  outlined
                  color="yellow darken-3">
                  <v-icon left style="font-size:19px;">mdi-package-variant-closed</v-icon>
                {{ order.thirdparty_order_number }}
              </v-chip>
            </router-link>
          </template>

          <!-- assigned vehicle -->
          <template v-slot:[`item.vehicle_id`]="{ item }">
            <span class="vehicle">{{ displayAssignedVehicle(item.vehicle) }}</span>
          </template>

          <!-- assigned driver -->
          <template v-slot:[`item.driver_user_id`]="{ item }">
            <span class="driver">{{ displayAssignedDriver(item.driver) }}</span>
          </template>

          <!-- created date/time -->
          <template v-slot:[`item.created_at`]="{ item }">
            <span>{{ formatDateTime(item.created_at) }}</span>
          </template>

          <!-- dispatched date/time -->
          <template v-slot:[`item.dispatched_at`]="{ item }">
            <span>{{ formatDateTime(item.dispatched_at) }}</span>
          </template>

          <!-- delivery started date/time -->
          <template v-slot:[`item.on_delivery_at`]="{ item }">
            <span>{{ formatDateTime(item.on_delivery_at) }}</span>
          </template>

          <!-- delivery completed date/time -->
          <template v-slot:[`item.completed_at`]="{ item }">
            <span>{{ formatDateTime(item.completed_at) }}</span>
          </template>

          <!-- delivery canceled date/time -->
          <template v-slot:[`item.canceled_at`]="{ item }">
            <span>{{ formatDateTime(item.canceled_at) }}</span>
          </template>

          <!-- edit route links -->
          <template v-slot:[`item.model`]="{ item }">
            <v-btn color="blue lighten-1" @click="editDispatchedRoute(item)" v-if="item.route_status === 'DISPATCHED_EDITING'">
              Finish Editing
            </v-btn>
            <!-- <v-btn color="blue lighten-1" @click="editDispatchedRoute(item)" v-else-if="item.route_status === 'DISPATCHED'">
              Edit Route
            </v-btn> -->
          </template>

          <!-- active delivery link -->
          <template v-slot:[`item.id`]="{ item }">
            <v-btn color="primary" @click="goToDeliveryPage(item)" v-if="item.route_status === 'DISPATCHED'">
              Start Delivery
            </v-btn>

            <v-tooltip top v-else-if="item.route_status === 'DISPATCHED_EDITING'">
              <template v-slot:activator="{ on, attrs }">
                <v-icon large color="grey darken-1" style="font-size:32px;cursor:pointer;" v-bind="attrs" v-on="on">mdi-update</v-icon>
              </template>
              <div>Route is being updated.</div>
            </v-tooltip>

            <v-btn color="primary" @click="goToDeliveryPage(item)" v-else>
              View Delivery
            </v-btn>
          </template>

          <!-- route status -->
          <template v-slot:[`item.route_status`]="{ item }">
            <v-chip label outlined :class="item.route_status">{{ item.route_status }}</v-chip>
          </template>
      </v-data-table>
    </div>

  </v-container>

</template>

<script>

export default {
  name: 'RouteListDataTable',

  props: {
    headers: {
      type: Array,
      default: () => [
        { text: 'Name', align: 'start', value: 'name' },
        { text: 'Orders', sortable: false, value: 'orders' },
        { text: 'Delivery Date', value: 'delivery_date' },
        { text: 'Est. Start', value: 'scheduled_start_time' },
        { text: 'Est. Return', value: 'estimated_return_time' },
        { text: 'Vehicle', value: 'vehicle_id' },
        { text: 'Driver', value: 'driver_user_id' },
        { text: 'Created', value: 'created_at' },
        { text: 'Status', align: 'start', value: 'route_status' }
      ]
    },
    status: {
      type: Array,
      default: () => null
    },
    assocs: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: 'Orders'
    },
    myRoutes: {
      type: Boolean,
      default: false
    },
    isSelectable: {
      type: Boolean,
      default: false
    },
    defaultSortField: {
      type: String,
      default: 'dispatched_at'
    },
    defaultSortOrder: {
      type: String,
      default: 'dsc' // dsc = descending, asc = ascending
    },
    routeClickGoTo: {
      type: String,
      default: 'route-builder' // options = route-builder, route-info, delivery-stepper
    }
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      routeData: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      dataOptions: {
        itemsPerPage: 10,
        // NOTE - Currently enabled for single field sort only !!!
        sortBy: [this.defaultSortField],
        sortDesc: (this.defaultSortOrder && this.defaultSortOrder === 'dsc') ? [true] : [false]
      },
      search: null,
      showTestRoutes: true,
      showLiveRoutes: true,
      selected: [],
      isRouteUpdating: false,
      routeUpdatingError: null
    }
  },

  computed: {
    activeTimezone () {
      return this.$store.getters['app/defaultTimezone']
    },
    routeItems () {
      return (this.routeData) ? this.routeData.items : []
    },
    dataMeta () {
      return (this.routeData) ? this.routeData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    }
  },

  watch: {
    // Observe changes invoked by the data-table component (e.g. pagination, sorting)
    dataOptions: {
      handler () {
        this.queryData()
      },
      deep: true
    },
    // Observe changes to the search field
    search: function (updated) {
      this.queryData()
    }
  },

  methods: {
    // Display a date
    formatDate (date) {
      return (date) ? this.$root.utils.format(date, 'MMM D, YYYY') : '-'
    },

    // Display a time
    formatTime (time) {
      return (time) ? this.$root.utils.format(time, 'h:mm a') : '-'
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.format(date, 'MMM D, YYYY h:mma') : ''
    },

    formatOrders (orders) {
      return (orders) ? orders.map(order => ` ${order.thirdparty_order_number}`).toString() : ''
    },

    displayAssignedVehicle (vehicle) {
      return (vehicle) ? vehicle.license_plate : '-'
    },

    displayAssignedDriver (driver) {
      return (driver) ? `${driver.first_name} ${driver.last_name}` : '-'
    },

    // Display location name
    formatLocation (loc) {
      return (loc && loc.name) ? loc.name : '-'
    },

    // Generate link data for route name click (per configured settings)
    // route builder page -or- route info page
    generateRouteClickPageLink (route) {
      switch (this.routeClickGoTo) {
        case 'route-builder': {
          return {
            name: 'route-builder',
            params: { routeId: route.id }
          }
        }
        case 'route-info': {
          return {
            name: 'route-info-overview',
            params: { routeId: route.id }
          }
        }
        case 'delivery-stepper': {
          return {
            name: 'delivery-stepper',
            params: { routeId: route.id }
          }
        }
      }
    },

    // Generate link data for order info page
    generateOrderInfoPageLink (order) {
      if (!order || !order.model) return ''
      const latestOrder = (order.order_history && order.order_history.length > 1)
        ? order.order_history[order.order_history.length - 1]
        : order

      return {
        name: 'order-info-overview',
        params: { orderId: latestOrder.id }
      }
    },

    // Enable dispatched Route for editing and go to the route builder page
    async editDispatchedRoute (item) {
      if (!item.id) return false

      if (item.route_status === 'DISPATCHED_EDITING') {
        // Go to route builder page
        this.$root.goTo('route-builder', {
          routeId: item.id
        })
      } else {
        // Enable for editing and go to builder page
        try {
          this.isRouteUpdating = true

          const updatedRoute = await this.$store.dispatch('sh/dataApi', {
            method: 'post',
            uri: `route/${item.id}/edit-dispatched`
          })

          // Go to route builder page
          this.$root.goTo('route-builder', {
            routeId: updatedRoute.id
          })
        } catch (err) {
          this.routeUpdatingError = err.message || err.text || err
        } finally {
          this.isRouteUpdating = false
        }
      }
    },

    // Go to the active delivery page (delivery stepper)
    goToDeliveryPage (item) {
      if (item && item.model) {
        this.$root.goTo('delivery-stepper', {
          routeId: item.id
        })
      } else {
        console.error('Invalid item data. Cannot invoke item page.')
      }
    },

    // Perform query of routes
    async queryData () {
      this.isDataLoading = true

      try {
        // Build sort
        // NOTE - Currently enabled for single field sort only !!!
        let orderBy = null
        const sortBy = this.dataOptions.sortBy
        const sortDesc = this.dataOptions.sortDesc
        if (sortBy && sortBy.length > 0) {
          const isDesc = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : false
          const prefix = (isDesc) ? '-' : ''
          orderBy = `${prefix}${sortBy[0]}`
        }

        // Build pagination
        const page = this.dataOptions.page || 1
        const limit = this.dataOptions.itemsPerPage
        const skip = (page === 1) ? 0 : (page - 1) * limit

        // Build request input
        const params = {
          skip,
          limit
        }

        // Apply sorting
        if (orderBy) params.order_by = orderBy

        // Apply filters
        if (this.status) params.status = this.status
        if (this.showTestRoutes && !this.showLiveRoutes) params.test_only = true
        if (this.showLiveRoutes && !this.showTestRoutes) params.live_only = true

        // Apply search query (for name only)
        if (this.search) params['name.contains'] = this.search

        // Include associations
        if (this.assocs) params.with = this.assocs

        // console.log('[DEVING] query data:', this.dataOptions, params)

        // Retrieve results
        const uri = (this.myRoutes) ? 'my/routes' : 'routes'
        this.routeData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri,
          opts: { params }
        })
      } catch (err) {
        this.dataLoadingError = err.message
      }

      this.isDataLoading = false
    }
  }
}
</script>

<style>
  .route-list-table .info-timezone {
    font-size: 1.25em;
    color: #6a6a6a;
    padding-bottom: 12px;
    cursor: pointer;
  }

  .route-list-table .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .route-list-table .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .route-list-table .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 75px;
  }

  .route-list-table span.v-chip {
    cursor: pointer;
  }

  .route-list-table .v-data-table a {
    text-decoration: none;
  }
  .route-list-table .v-data-table a:hover {
    text-decoration: underline;
  }
</style>
